import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePushNotifications } from '../contexts/PushNotificationContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { User, Lock, CreditCard, Bell, Mail, Loader2, CheckCircle2, XCircle, Code } from 'lucide-react';
import APIIntegrationPanel from './APIIntegrationPanel';
import NotificationPreferences from './NotificationPreferences';

const SettingsModal = ({ open, onClose }) => {
  const { user, changePassword, updateProfile } = useAuth();
  const { 
    isSupported, 
    isSubscribed, 
    permission, 
    subscribe, 
    unsubscribe, 
    sendTestNotification,
    loading: pushLoading 
  } = usePushNotifications();
  const [loading, setLoading] = useState(false);
  
  // Profile form
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [contactNo, setContactNo] = useState(user?.contact_no || '');
  
  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateProfile({ first_name: firstName, last_name: lastName, contact_no: contactNo });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    
    setLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableNotifications = async () => {
    setLoading(true);
    try {
      await subscribe([]);
      toast.success('Push notifications enabled!');
    } catch (error) {
      toast.error(error.message || 'Failed to enable notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    setLoading(true);
    try {
      await unsubscribe();
      toast.success('Push notifications disabled');
    } catch (error) {
      toast.error(error.message || 'Failed to disable notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = async () => {
    setLoading(true);
    try {
      await sendTestNotification();
      toast.success('Test notification sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test notification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto" data-testid="settings-modal">
        <DialogHeader>
          <DialogTitle className="text-xl">Settings</DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="profile" className="mt-4">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">Profile</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              <span className="hidden sm:inline">Security</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              <span className="hidden sm:inline">Alerts</span>
            </TabsTrigger>
            {(user?.role === 'admin' || user?.role === 'super_admin') && (
              <TabsTrigger value="api" className="flex items-center gap-2" data-testid="settings-api-tab">
                <Code className="h-4 w-4" />
                <span className="hidden sm:inline">API</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="subscription" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">Plan</span>
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">Help</span>
            </TabsTrigger>
          </TabsList>
          
          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-4 mt-4">
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    data-testid="profile-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    data-testid="profile-last-name"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  value={user?.email || ''}
                  disabled
                  className="bg-muted"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contactNo">Contact Number</Label>
                <Input
                  id="contactNo"
                  value={contactNo}
                  onChange={(e) => setContactNo(e.target.value)}
                  data-testid="profile-contact"
                />
              </div>
              <Button type="submit" disabled={loading} data-testid="save-profile-btn">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </form>
          </TabsContent>
          
          {/* Security Tab */}
          <TabsContent value="security" className="space-y-4 mt-4">
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  data-testid="current-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  data-testid="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  data-testid="confirm-password"
                />
              </div>
              <Button type="submit" disabled={loading} data-testid="change-password-btn">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Change Password
              </Button>
            </form>
          </TabsContent>
          
          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-4 mt-4">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Push Notifications</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Get notified when new orders arrive, even when the app is in background.
                </p>
              </div>
              
              {!isSupported ? (
                <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                  <XCircle className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Not Supported</p>
                    <p className="text-sm text-muted-foreground">
                      Push notifications are not supported in this browser.
                    </p>
                  </div>
                </div>
              ) : permission === 'denied' ? (
                <div className="flex items-center gap-3 p-4 bg-red-500/10 rounded-lg">
                  <XCircle className="h-5 w-5 text-red-500" />
                  <div>
                    <p className="font-medium text-red-600">Permission Denied</p>
                    <p className="text-sm text-muted-foreground">
                      Please enable notifications in your browser settings.
                    </p>
                  </div>
                </div>
              ) : isSubscribed ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-emerald-500/10 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                    <div>
                      <p className="font-medium text-emerald-600">Notifications Enabled</p>
                      <p className="text-sm text-muted-foreground">
                        You'll receive alerts for new orders.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={handleTestNotification}
                      disabled={loading}
                      data-testid="test-notification-btn"
                    >
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Send Test
                    </Button>
                    <Button 
                      variant="outline" 
                      className="text-red-600"
                      onClick={handleDisableNotifications}
                      disabled={loading}
                      data-testid="disable-notifications-btn"
                    >
                      Disable Notifications
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                    <Bell className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Notifications Disabled</p>
                      <p className="text-sm text-muted-foreground">
                        Enable to receive order alerts.
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={handleEnableNotifications}
                    disabled={loading}
                    data-testid="enable-notifications-btn"
                  >
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <Bell className="mr-2 h-4 w-4" />
                    Enable Notifications
                  </Button>
                </div>
              )}
            </div>
            
            {/* Notification Preferences */}
            <NotificationPreferences />
          </TabsContent>
          
          {/* API Integration Tab */}
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <TabsContent value="api" className="mt-4" data-testid="api-integration-tab">
              <APIIntegrationPanel />
            </TabsContent>
          )}
          
          {/* Subscription Tab */}
          <TabsContent value="subscription" className="space-y-4 mt-4">
            <div className="text-center py-8">
              <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Manage Subscription</h3>
              <p className="text-muted-foreground mb-4">
                View and manage your subscription plan from the dashboard.
              </p>
            </div>
          </TabsContent>
          
          {/* Contact Tab */}
          <TabsContent value="contact" className="space-y-4 mt-4">
            <div className="text-center py-8">
              <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Need Help?</h3>
              <p className="text-muted-foreground mb-4">
                Contact our support team for assistance.
              </p>
              <p className="text-sm">
                Email: <a href="mailto:support@deliman.com" className="text-primary hover:underline">support@deliman.com</a>
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsModal;
